import { StandaloneSearchBox, useJsApiLoader } from "@react-google-maps/api";
import { FormikProps } from "formik";
import React, { useRef, useState } from "react";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/config";
import { checkDistance } from "../../utils/helpers";
import Button from "../Button";
import Input from "../Input";
import MapContainer from "../Map";
import { MainFormType } from "./CreateServiceModal";
import { checkState } from '../../apis/services/create-service';

interface DropoffInformationProps {
  form: FormikProps<MainFormType>;
}

type InvalidLocationType = {
  invalid: boolean;
  reason: "same-location" | "invalid-distance" | "";
};

const DropoffInformation = ({ form }: DropoffInformationProps) => {
  const [isInvalidLocation, setIsInvalidLocation] =
    useState<InvalidLocationType>({
      invalid: false,
      reason: "",
    });

  const [loading, setLoading] = useState(false);
  const values = form.values[form.values.serviceType!]?.dropoffInformation;
  const errors =
    form.errors[form.values.serviceType!]?.dropoffInformation || {};

  const handleFieldChange = (field: string, value: string | number) => {
    form.setFieldValue(
      `${form.values.serviceType!}.dropoffInformation.${field}`,
      value
    );
  };

  const handleDropoffInformationSubmit = async () => {
    console.log("handle drop off information subbmit called");
    // Validate dropoff information fields
    setLoading(true)
    const dropoffErrors: any = {};
    const dropoffInfo =
      form.values[form.values.serviceType!].dropoffInformation;

    if (!dropoffInfo.lat) dropoffErrors.lat = "Please select a dropff location";
    if (!dropoffInfo.lng) dropoffErrors.lng = "Please select a dropff location";
    if (!dropoffInfo.address)
      dropoffErrors.address = "Please select a dropoff location";

    if (Object.keys(dropoffErrors).length === 0) {
      // check if pickup and dropoff location is same
      const pickupLat =
        form.values[form.values.serviceType!].pickupInformation.lat;
      const pickupLng =
        form.values[form.values.serviceType!].pickupInformation.lng;
      const lat = values.lat;
      const lng = values.lng;


      try {
        await checkState(
          form.values[form.values.serviceType!].pickupInformation?.state
        );
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }

      if (lat === pickupLat && lng === pickupLng) {
        setIsInvalidLocation({
          invalid: true,
          reason: "same-location",
        });
        return;
      }
      const isDistanceValid = checkDistance(
        {
          latitude: pickupLat!,
          longitude: pickupLng!,
        },
        {
          latitude: lat!,
          longitude: lng!,
        }
      );
      if (!isDistanceValid) {
        setIsInvalidLocation({
          invalid: true,
          reason: "invalid-distance",
        });
        return;
      }

      // check if pickup and dropoff information are within max distance radius
      form.setFieldValue("step", "details");
    } else {
      // Set touched and errors for dropoff information fields
      form.setTouched({
        ...form.touched,
        [form.values.serviceType!]: {
          ...form.touched[form.values.serviceType!],
          dropoffInformation: {
            lat: true,
            lng: true,
            address: true,
          },
        },
      });

      form.setErrors({
        ...form.errors,
        [form.values.serviceType!]: {
          ...form.errors[form.values.serviceType!],
          dropoffInformation: dropoffErrors,
        },
      });
    }
  };

  const inputRef = useRef<google.maps.places.SearchBox | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY!,
    libraries: GOOGLE_MAPS_LIBRARIES!,
  });

  const handlePlacesChanged = () => {
    const address = inputRef.current?.getPlaces();
    const lat = address?.[0]?.geometry?.location?.lat();
    const lng = address?.[0]?.geometry?.location?.lng();

    const country = address?.[0]?.address_components?.find((comp) =>
      comp?.types?.includes("country")
    )?.long_name;

    const city = address?.[0]?.address_components?.find((comp) =>
      comp?.types?.includes("locality")
    )?.long_name;

    handleFieldChange("address", address?.[0]?.formatted_address!);
    handleFieldChange("lat", lat!);
    handleFieldChange("lng", lng!);
    handleFieldChange("country", country!);
    handleFieldChange("city", city!);
  };

  const hasError = errors.address || errors.lng || errors.lat;

  console.log(isInvalidLocation, "is locatiin invalid");

  return (
    <div className="flex flex-col gap-2">
      {isLoaded && !isInvalidLocation.invalid ? (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleDropoffInformationSubmit();
            }}
          >
            <p className="text-center text-lg font-semibold">
              Dropoff information
            </p>
            <p className="text-center">Please enter dropoff address</p>
            <StandaloneSearchBox
              onPlacesChanged={handlePlacesChanged}
              onLoad={(ref) => (inputRef.current = ref)}
            >
              <Input
                value={values.address}
                onChange={(e) => handleFieldChange("address", e.target.value)}
                name="address"
                autoFocus
                label="Dropoff location *"
                placeholder="Enter address"
                className='lg:text-[1rem] my-2'
                labelClassName='lg:text-[1rem]'
              />
            </StandaloneSearchBox>
            {values.lat && values.lng && (
              <div className="w-full h-96 mt-4 rounded-xl overflow-hidden">
                <MapContainer
                  zoom={20}
                  handleMapClick={async (event) => {
                    handleFieldChange("lat", event.latLng?.lat()!);
                    handleFieldChange("lng", event.latLng?.lng()!);

                    const geocoder = new window.google.maps.Geocoder();
                    try {
                      const { results } = await geocoder.geocode({
                        location: event.latLng,
                      });

                      if (results[0]) {
                        const country = results?.[0]?.address_components?.find(
                          (comp) => comp?.types?.includes("country")
                        )?.long_name;

                        const city = results?.[0]?.address_components?.find(
                          (comp) => comp?.types?.includes("locality")
                        )?.long_name;

                        handleFieldChange("city", city!);
                        handleFieldChange("country", country!);

                        handleFieldChange(
                          "address",
                          results[0].formatted_address
                        );
                      }
                    } catch (error) { }
                  }}
                  latt={values.lat}
                  lngg={values.lng}
                />
              </div>
            )}
            {hasError ? (
              <p className="text-xs text-red-600">
                Please enter dropoff location
              </p>
            ) : null}
            <div className="flex justify-center mt-3 md:mr-3">
              <Button
                type="submit"
                className="w-full md:w-32 min-h-0 h-10 md:h-10"
                isLoading={loading}
              >
                Continue
              </Button>
            </div>
          </form>
        </>
      ) : (
        <div className="flex flex-col items-center my-10 gap-4">
          <p className="text-primary text-2xl font-bold">
            {isInvalidLocation.reason === "same-location"
              ? "Invalid location"
              : "Sorry!"}
          </p>
          <p className="font-medium text-center">
            {isInvalidLocation.reason === "same-location"
              ? "Pickup and dropoff location's cannot be same."
              : "Swyvery only provides delivery services for stores that are in a 40-mile radius."}
          </p>
          <p className="text-sm">
            {isInvalidLocation.reason === "same-location"
              ? "Please select different pickup and dropoff locations."
              : "Please go back and select a store that is in your range."}
          </p>
          <Button
            className="w-full md:w-32 min-h-0 h-10 md:h-10"
            onClick={() => {
              setIsInvalidLocation({
                invalid: false,
                reason: "",
              });
            }}
          >
            Go back
          </Button>
        </div>
      )}
    </div>
  );
};

export default DropoffInformation;
